import { partition } from 'lodash';
import {
  nodeTypes as getNodeTypes,
  allValidInputTypesForNodeType,
  modelTypes as getModelTypes,
  allValidInputTypesForModelType,
  type DataType,
  type NodeType,
  type ModelNodeType,
} from 'web';
import { HandleType } from '@/enums/handle-type.enum';
import { NodeType as NodeTypeEnum } from '@/enums/node-type.enum';
import { searchAndSort } from '@/utils/search';
import { MenuCategory, MenuData, ModelItem } from './nodes.types';
import type { Node } from '@/types/node';
import type { NodeDefinition } from '@/types/api/nodeDefinition';

export const flattenMenuData = (menu: Partial<MenuData>): ModelItem[] => {
  let items: ModelItem[] = [];
  for (const key in menu) {
    const category = menu[key as keyof MenuData];
    if (category && 'children' in category && Array.isArray(category.children)) {
      items = items.concat(flattenMenuCategories([category]));
    }
  }
  // Deduplicate by id (keep first occurrence)
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
};

export const flattenMenuCategories = (categories: MenuCategory[]): ModelItem[] => {
  const result: ModelItem[] = [];
  for (const cat of categories) {
    const [withChildren, withoutChildren] = partition(cat.children, (child) => 'children' in child);
    result.push(...flattenMenuCategories(withChildren));
    result.push(...withoutChildren);
  }
  return result;
};

/**
 * Filters a list of ModelItem by a search string, matching displayName, description, or searchText (case-insensitive).
 * Uses enhanced search with relevance scoring for better results.
 */
export function filterNodesBySearch(items: ModelItem[], search: string): ModelItem[] {
  if (!search) return items;

  return searchAndSort(items, search, (item) => [item.displayName || '', item.searchText || '']);
}

export interface WasmFilterOverrides {
  wasmItemIds: Set<ModelItem['id']>;
  wasmKnownIds: Set<ModelItem['id']>;
}

/** Wildcard input types belong to each model's schema, not its static WASM kind. */
export function getWasmKnownItemIds(
  nodeKindById: Map<ModelItem['id'], NodeType>,
  modelKindById: Map<ModelItem['id'], ModelNodeType>,
): Set<ModelItem['id']> {
  return new Set([
    ...nodeKindById.keys(),
    ...[...modelKindById].filter(([, kind]) => kind !== 'wildcard').map(([id]) => id),
  ]);
}

/**
 * Filters a list of ModelItem by inputTypes/outputTypes and side of requested connection.
 * When `wasm` is provided, WASM-known items bypass backend types (used for input-type
 * filtering where backend data is unreliable, e.g. Crop declares "any" instead of
 * [image, mask, video]).
 */
export function filterNodesByInputOutputTypes(
  items: ModelItem[],
  typeKey: keyof Pick<ModelItem, 'inputTypes' | 'outputTypes'>,
  types: HandleType[],
  wasm?: WasmFilterOverrides,
): ModelItem[] {
  if (!items?.length) {
    return items;
  }

  // Drop items with no handles on the requested side (e.g. preview and export have no outputs).
  // WASM-known items are kept even when their backend types are empty: their real handle types live
  // in WASM (see the override below), which the backend can't derive for nodes with dynamic handles
  // like the Compositor. WASM is only supplied on the input-type path, so preview/export still stay
  // hidden on the output side where they genuinely have no handles.
  const res = items.filter((item) => Boolean(item[typeKey]?.length) || Boolean(wasm?.wasmKnownIds.has(item.id)));

  if (!types || !types.length) {
    return res;
  }

  // Kling Element / Keyframe skip WASM: only nodes that declare the type on the backend
  // should appear (WASM's "any" set would otherwise list Router, Crop, …). `res` still
  // contains WASM-known items with missing types (Gen Effect, Compositor), so the lookup
  // has to tolerate undefined — otherwise dragging those outputs crashes the add-node menu.
  if (types.length === 1 && (types[0] === HandleType.KlingElement || types[0] === HandleType.Keyframe)) {
    return res.filter((item) => types.every((type) => item[typeKey]?.includes(type)));
  }

  return res.filter((item) => {
    // If WASM knows this item, trust WASM as the source of truth
    if (wasm && wasm.wasmKnownIds.has(item.id)) {
      return wasm.wasmItemIds.has(item.id);
    }
    // For items WASM doesn't know (or when WASM is not used), fall back to backend types
    return item[typeKey]?.includes(HandleType.Any) || types.every((type) => item[typeKey]?.includes(type));
  });
}

/**
 * Returns the set of menu item IDs whose kind accepts ALL of the given handle types (AND
 * semantics), using WASM as the source of truth. Used by multi-connect to filter the menu to
 * nodes compatible with every dragged source type.
 *
 * Backend `inputTypes` are unreliable (e.g., Crop declares "any" instead of [image, mask, video]
 * because the DB stores a single type per handle), so we look up valid types via WASM instead.
 */
export function getWasmItemIds(
  handleTypes: HandleType[],
  nodeKindById: Map<ModelItem['id'], NodeType>,
  modelKindById: Map<ModelItem['id'], ModelNodeType>,
): Set<ModelItem['id']> {
  if (handleTypes.length === 0) return new Set();

  const dataTypes = handleTypes.map((h) => h as unknown as DataType);
  const compatibleNodeKinds = new Set<NodeType>(
    getNodeTypes().filter((nt) => {
      const valid = allValidInputTypesForNodeType(nt);
      return dataTypes.every((dt) => valid.includes(dt));
    }),
  );
  const compatibleModelKinds = new Set<ModelNodeType>(
    getModelTypes().filter((mt) => {
      const valid = allValidInputTypesForModelType(mt);
      return dataTypes.every((dt) => valid.includes(dt));
    }),
  );

  const ids = new Set<ModelItem['id']>();
  for (const [id, kind] of nodeKindById) {
    if (compatibleNodeKinds.has(kind)) ids.add(id);
  }
  for (const [id, kind] of modelKindById) {
    if (compatibleModelKinds.has(kind)) ids.add(id);
  }
  return ids;
}

// Map a canvas node back to its menu id. Production catalog entries can be keyed by UUID, so
// `node.type` (the rendering type) isn't enough — look up the actual id in `nodeTypes`.
// Multiple catalog entries share a rendering type (every AI model is `custommodelV2`; muxv2 covers
// the text iterator and the list selector); discriminate by data fields before falling back to
// the first-match lookup. The NodeTypeEnum constants below are how this function survives a future
// version bump (e.g. PromptV3 → PromptV4) — update the enum and call sites move with it.
export const resolveNodeMenuId = (node: Node, nodeTypes: NodeDefinition[]): string => {
  const data = node.data as
    | {
        kind?: { type?: unknown; model?: { name?: unknown } };
        model?: { name?: unknown };
        acceptedFileType?: unknown;
        isIterator?: unknown;
      }
    | undefined;

  if (node.type === NodeTypeEnum.MediaIterator) {
    if (data?.acceptedFileType === 'image') {
      return 'image_iterator';
    }
    if (data?.acceptedFileType === 'video') {
      return 'video_iterator';
    }
  }

  if (node.type === NodeTypeEnum.MuxV2) {
    if (data?.isIterator === true) {
      return 'text_iterator';
    }
    const listSelectorDef = nodeTypes.find(
      (d) =>
        (d.type as NodeTypeEnum) === NodeTypeEnum.MuxV2 && (d.data as { isIterator?: unknown })?.isIterator !== true,
    );
    if (listSelectorDef) {
      return listSelectorDef.id;
    }
  }

  // AI model nodes all render as `custommodelV2`, so disambiguate by the underlying model name.
  // Wildcard kinds carry it under `data.kind.model.name`; non-wildcard nodes keep the legacy
  // `data.model.name` cloned from the catalog entry's data.
  if (node.type === NodeTypeEnum.CustomModelV2) {
    const kindModelName = typeof data?.kind?.model?.name === 'string' ? data.kind.model.name : undefined;
    const legacyModelName = typeof data?.model?.name === 'string' ? data.model.name : undefined;
    const modelName = kindModelName ?? legacyModelName;
    if (modelName) {
      const def = nodeTypes.find(
        (d) =>
          (d.type as NodeTypeEnum) === NodeTypeEnum.CustomModelV2 &&
          (d.data as { model?: { name?: unknown } })?.model?.name === modelName,
      );
      if (def) {
        return def.id;
      }
    }
  }

  const def = nodeTypes.find((d) => d.type === node.type);
  return def?.id ?? node.type ?? '';
};
